import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import axios from 'axios'
import { deleteTariffApiErrors } from './dto/tariffs-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

type DeleteTariffParams = {
  installationId: number
  tariffId: string
}

interface UseDeleteTariffHook {
  isLoading: boolean
  deleteTariff: ({ installationId, tariffId }: DeleteTariffParams) => void
}

/**
 * Function for delete tariff data
 * @param tariffId - - unique tariff identifier as a string
 * @param onClose - function for closing dialog on success method
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useDeleteTariff = (onClose: () => void): UseDeleteTariffHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const deleteTariffApi = async ({ installationId, tariffId }: DeleteTariffParams): Promise<ApiResult<string>> => {
    const tariffResponse = await authFetch<string>({
      path: `tariffs/${tariffId}/installation/${Number(installationId)}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'DELETE',
    })

    return new ApiResult(tariffResponse)
  }

  const { isPending, mutate: deleteTariff } = usePortalMutation({
    mutationFn: deleteTariffApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(deleteTariffApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.tariffs.delete-tariff-failed'))
      }
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['tariffs', variables.installationId] })
      enqueueSuccessStack(t('emobility.tariffs.delete-tariff-success'))
      onClose()
    },
  })

  return { isLoading: isPending, deleteTariff }
}
